/**
 * 项目内 GLB 资产下载与解析服务。
 *
 * 设计说明：
 * 1. 本模块处理的是当前 CAD 会话中已经存在于项目文件列表里的 `.glb` 资产；
 * 2. 这里的 helper 不代表支持浏览器本地文件选择或本地单文件导入；
 * 3. 现阶段仍保留这些方法，是为了兼容现有导入模型运行时与旧调用方。
 */
import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import type { FileInfo } from '@puppycad/proto'
import { logger } from '@/utils'
import { downloadCadFile } from '@/services/api/cad-session'
import {
  addGlbResultToScene,
  applyOriginalNodeNamesToScene,
  cloneWithSharedGeometry,
  collectGlbHierarchy,
  extractPartName,
  ImportedModelObjectMetadataService,
  parseGlb,
  type GlbLoadResult,
} from '@puppycad/cad-core'
import {
  isImportedModelLoadAbortedError,
  throwIfImportedModelRequestStale,
  type ImportedModelLoadGuard,
} from './imported-model-load-guard'

/**
 * GLB 诊断摘要。
 */
type GlbDiagnosticSummary = {
  rootName: string
  rootType: string
  childCount: number
  nodeCount: number
  meshCount: number
  materialCount: number
  materialNames: string[]
  vertexCount: number
  triangleCount: number
  emptyGeometryMeshCount: number
  bounds: {
    empty: boolean
    min: [number, number, number] | null
    max: [number, number, number] | null
    size: [number, number, number] | null
    center: [number, number, number] | null
  }
}

/**
 * 将数值保留 3 位小数，避免调试日志出现过长浮点噪声。
 *
 * @param value 原始数值。
 * @returns 保留 3 位小数后的结果。
 */
function round3(value: number): number {
  return Number(value.toFixed(3))
}

/**
 * 将向量转成日志友好的三元组。
 *
 * @param vector 目标向量。
 * @returns `[x, y, z]` 三元组。
 */
function toTuple(vector: THREE.Vector3): [number, number, number] {
  return [round3(vector.x), round3(vector.y), round3(vector.z)]
}

/**
 * 汇总 GLB 场景对象的几何诊断信息。
 *
 * @param root GLB 场景根对象。
 * @returns 诊断摘要。
 */
function summarizeGlbObject(root: THREE.Object3D): GlbDiagnosticSummary {
  let nodeCount = 0
  let meshCount = 0
  let vertexCount = 0
  let triangleCount = 0
  let emptyGeometryMeshCount = 0
  const materialNames = new Set<string>()

  /**
   * 递归遍历场景对象树，统计几何和材质信息。
   *
   * @param object 当前对象。
   */
  const walk = (object: THREE.Object3D) => {
    nodeCount += 1

    if (object instanceof THREE.Mesh) {
      meshCount += 1
      const geometry = object.geometry
      const position = geometry?.getAttribute('position')
      const positionCount = position?.count ?? 0
      const indexCount = geometry?.index?.count ?? 0
      const nextTriangleCount = indexCount > 0 ? indexCount / 3 : positionCount / 3
      if (!position || position.count === 0) {
        emptyGeometryMeshCount += 1
      } else {
        vertexCount += position.count
        triangleCount += nextTriangleCount
      }

      const materials = Array.isArray(object.material) ? object.material : [object.material]
      materials.forEach((material) => {
        const name = material?.name?.trim()
        if (name) {
          materialNames.add(name)
        }
      })
    }

    object.children.forEach((child) => walk(child))
  }

  root.updateWorldMatrix(true, true)
  walk(root)

  const bounds = new THREE.Box3().setFromObject(root)
  const boundsEmpty = bounds.isEmpty()

  return {
    rootName: root.name || '(unnamed)',
    rootType: root.type,
    childCount: root.children.length,
    nodeCount,
    meshCount,
    materialCount: materialNames.size,
    materialNames: Array.from(materialNames).slice(0, 20),
    vertexCount,
    triangleCount,
    emptyGeometryMeshCount,
    bounds: {
      empty: boundsEmpty,
      min: boundsEmpty ? null : toTuple(bounds.min),
      max: boundsEmpty ? null : toTuple(bounds.max),
      size: boundsEmpty ? null : toTuple(bounds.getSize(new THREE.Vector3())),
      center: boundsEmpty ? null : toTuple(bounds.getCenter(new THREE.Vector3())),
    },
  }
}

/**
 * 基于已经解析完成的 GLTF 构建旧版 GLB 结果结构。
 *
 * @param gltf 已完成解析的 GLTF。
 * @param file 项目文件列表中的 GLB 资产信息。
 * @returns 兼容旧运行时的 GLB 结果。
 */
export function buildLegacyGlbResultFromParsedGltf(
  gltf: GlbLoadResult['gltf'],
  file: FileInfo,
): GlbLoadResult {
  const partName = extractPartName(file.fileId)
  applyOriginalNodeNamesToScene(gltf)
  const object = cloneWithSharedGeometry(gltf.scene) as THREE.Group
  const summary = summarizeGlbObject(object)

  logger.info(`[glb-download] GLB 对象摘要: ${partName}`, summary)
  if (summary.bounds.empty) {
    logger.warn('[glb-download] GLB 包围盒为空，可能导致 imported-assembly 对焦失败:', {
      fileId: file.fileId,
      uuid: file.uuid,
      partName,
      summary,
    })
  }

  object.name = partName
  ImportedModelObjectMetadataService.writeMetadata(object, {
    fileInfo: { fileId: file.fileId, uuid: file.uuid, partName },
  })
  const { hierarchy, rows } = collectGlbHierarchy(object, file)

  logger.info(`[glb-download] 解析完成: ${partName}`)
  return {
    kind: 'legacy-glb',
    fileInfo: file,
    partName,
    object,
    gltf,
    hierarchy,
    hierarchyRows: rows,
  }
}

/**
 * 下载并解析当前项目会话中的单个 GLB 资产。
 *
 * 说明：
 * 1. 这里的文件来源是项目文件系统中的 `FileInfo`，不是浏览器本地 `File`；
 * 2. 该方法可以保留，但不应被解释为“支持本地 GLB 导入”。
 *
 * @param sessionId 当前 CAD 会话 ID。
 * @param file 项目文件列表中的 GLB 资产信息。
 * @param guard 导入模型加载守卫，用于中止过期请求。
 * @returns 解析结果；失败时返回 `null`。
 */
export async function downloadAndParseGlb(
  sessionId: string,
  file: FileInfo,
  guard?: ImportedModelLoadGuard,
): Promise<GlbLoadResult | null> {
  const loader = new GLTFLoader()
  const partName = extractPartName(file.fileId)

  try {
    throwIfImportedModelRequestStale(guard)
    const data = await downloadCadFile(sessionId, file.fileId, file.uuid)
    throwIfImportedModelRequestStale(guard)
    logger.info(`[glb-download] 下载完成，开始解析 GLB: ${partName}`)

    const gltf = await parseGlb(loader, data)
    throwIfImportedModelRequestStale(guard)
    return buildLegacyGlbResultFromParsedGltf(gltf, file)
  } catch (err) {
    if (isImportedModelLoadAbortedError(err)) {
      throw err
    }
    logger.error(`[glb-download] 加载失败: ${partName}`, err)
    return null
  }
}

/**
 * 将单个项目内 GLB 资产直接挂载到场景。
 *
 * 说明：
 * 1. “单个”仅表示项目内的单个资产，不表示支持本地单文件加载；
 * 2. 该函数只是基于项目会话下载结果做场景挂载的便利 helper。
 *
 * @param sessionId 当前 CAD 会话 ID。
 * @param file 项目文件列表中的 GLB 资产信息。
 * @param scene 目标场景。
 * @returns 挂载后的解析结果；失败时返回 `null`。
 */
export async function loadSingleGlb(
  sessionId: string,
  file: FileInfo,
  scene: THREE.Scene,
): Promise<GlbLoadResult | null> {
  const result = await downloadAndParseGlb(sessionId, file)
  if (!result) {
    return null
  }

  addGlbResultToScene(result, scene)

  logger.info(`[glb-download] 已添加到场景: ${result.partName}`)
  return result
}
